
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { auth } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SignatureCanvas from "react-signature-canvas";

const Feedback = () => {
  const { trainingId } = useParams();
  const currentUser = auth.currentUser;
  const signaturePadRef = useRef(null);

  const [learnerName, setLearnerName] = useState(currentUser?.displayName || "Loading...");
  const [courseDate, setCourseDate] = useState("");
  const [courseName, setCourseName] = useState("");
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false); // Track if feedback exists
  const [savedSignature, setSavedSignature] = useState(null); // Save the retrieved signature
  const [submittedDate, setSubmittedDate] = useState(""); // Store submitted date
  const [userResponses, setUserResponses] = useState({
    learnerName: currentUser?.displayName || "Loading...",
    organisation: "Bee's Care Ltd",
    courseName: "",
    courseDate: "",
    courseContent: "",
    courseOverall: "",
    courseInformation: "",
    presentationStyle: "",
    materialsProvided: "",
    difficultyUnderstandingParts: "",
    easyUnderstandingParts: "",
    usefulParts: "",
    additionalFeedback: "",
  });

  useEffect(() => {
    if (currentUser) {
      setLearnerName(currentUser.displayName);
    }

    const fetchFeedbackData = async () => {
      if (!trainingId || !currentUser) return;

      try {
        const feedbackRef = doc(db, "users", currentUser.uid, "feedback", trainingId);
        const feedbackDoc = await getDoc(feedbackRef);

        if (feedbackDoc.exists()) {
          const feedbackData = feedbackDoc.data();
          setUserResponses(feedbackData);
          setIsFeedbackSubmitted(true); // Mark feedback as submitted
          setSavedSignature(feedbackData.signature || null); // Set the saved signature
          setSubmittedDate(feedbackData.submittedDate || ""); // Set the saved submitted date
        }
      } catch (error) {
        toast.error("Error fetching feedback data: " + error.message);
      }
    };

    const fetchQuizData = async () => {
      if (!trainingId || !currentUser) return;

      try {
        const quizDocRef = doc(db, "users", currentUser.uid, "quizzes", trainingId);
        const quizDoc = await getDoc(quizDocRef);

        if (quizDoc.exists()) {
          const quizData = quizDoc.data();

          setCourseName(quizData.trainingName || "No course name available");

          // Convert Firestore Timestamp to a valid date format
          if (quizData.completedAt) {
            const completedDate = quizData.completedAt.toDate();
            const formattedDate = completedDate.toISOString().split("T")[0];
            setCourseDate(formattedDate);
          } else {
            setCourseDate("No completion date available");
          }

          setUserResponses((prevState) => ({
            ...prevState,
            courseName: quizData.trainingName || "No course name available",
            courseDate: quizData.completedAt
              ? quizData.completedAt.toDate().toISOString().split("T")[0]
              : "No completion date available",
          }));
        } else {
          toast.error("No quiz data found for the provided training ID.");
        }
      } catch (error) {
        toast.error("Error fetching quiz data: " + error.message);
      }
    };

    fetchFeedbackData();
    fetchQuizData();
  }, [currentUser, trainingId]);

  const handleInputChange = (field, value) => {
    setUserResponses((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    const requiredFields = [
      "learnerName",
      "organisation",
      "courseName",
      "courseDate",
      "courseContent",
      "courseOverall",
      "courseInformation",
      "presentationStyle",
      "materialsProvided",
      "difficultyUnderstandingParts",
      "easyUnderstandingParts",
      "usefulParts",
      "additionalFeedback",
    ];

    for (let field of requiredFields) {
      if (!userResponses[field] || userResponses[field].trim() === "") {
        toast.error(`Please fill in the required field: ${field}`);
        return false;
      }
    }

    if (signaturePadRef.current.isEmpty()) {
      toast.error("Please provide your signature before submitting.");
      return false;
    }

    return true;
  };

  const handleSubmitFeedback = async () => {
    if (isFeedbackSubmitted) {
      toast.error("Feedback has already been submitted.");
      return;
    }

    if (!trainingId) {
      toast.error("Training ID is missing.");
      return;
    }

    if (!currentUser) {
      toast.error("Please log in to submit feedback.");
      return;
    }

    if (!isFormValid()) {
      return;
    }

    const signatureDataURL = signaturePadRef.current.getTrimmedCanvas().toDataURL();
    const currentDate = new Date().toISOString().split("T")[0]; // Get today's date

    try {
      const feedbackRef = doc(db, "users", currentUser.uid, "feedback", trainingId);

      await setDoc(feedbackRef, {
        ...userResponses,
        signature: signatureDataURL,
        submittedDate: currentDate, // Save the submitted date
      });

      toast.success("Feedback submitted successfully!");
      setIsFeedbackSubmitted(true); // Mark feedback as submitted
      setSavedSignature(signatureDataURL); // Save the newly submitted signature
      setSubmittedDate(currentDate); // Set the submitted date
    } catch (error) {
      toast.error("Error submitting feedback: " + error.message);
    }
  };

  return (
    <div className="container mt-3">
      <div className="text-center mb-4">
        <img className="logo-img" src="trainer-logo.png" alt="Bee's Care Ltd" />
        <h2 className="mt-5">{courseName} Feedback Form</h2>
      </div>

      <form>
        <div className="mb-3">
          <label><strong>Name of Learner:</strong></label>
          <input type="text" value={learnerName} className="form-control" readOnly />
        </div>

        <div className="mb-3">
          <label><strong>Organisation:</strong></label>
          <input type="text" value="Bee's Care Ltd" className="form-control" readOnly />
        </div>

        <div className="mb-3">
          <label><strong>Course Date:</strong></label>
          <input type="date" value={courseDate} className="form-control" readOnly />
        </div>

        <div className="mb-3">
          <label><strong>Course Name:</strong></label>
          <input type="text" value={userResponses.courseName} className="form-control" readOnly />
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th></th>
              <th>Very Poor</th>
              <th>Poor</th>
              <th>Good</th>
              <th>Very Good</th>
              <th>Excellent</th>
            </tr>
          </thead>
          <tbody>
            {["courseContent", "courseOverall", "courseInformation", "presentationStyle", "materialsProvided"].map(
              (field, index) => (
                <tr key={index}>
                  <td>The {field.replace(/([A-Z])/g, " $1").toLowerCase()} was:</td>
                  {["Very Poor", "Poor", "Good", "Very Good", "Excellent"].map((rating) => (
                    <td
                      key={rating}
                      className={`text-center ${userResponses[field] === rating ? "bg-secondary text-white" : ""}`}
                      onClick={!isFeedbackSubmitted ? () => handleInputChange(field, rating) : undefined}
                      style={{ cursor: isFeedbackSubmitted ? "not-allowed" : "pointer" }}
                    >
                      {userResponses[field] === rating ? "✔" : ""}
                    </td>
                  ))}
                </tr>
              )
            )}
          </tbody>
        </table>

        <div className="mb-3">
          <label className="form-label">
            Were there any parts of the course that you found difficult to understand? If so, can you elaborate on this?
          </label>
          <textarea
            className="form-control"
            rows="4"
            value={userResponses.difficultyUnderstandingParts}
            onChange={(e) => handleInputChange("difficultyUnderstandingParts", e.target.value)}
            readOnly={isFeedbackSubmitted}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Were there any parts of the course that you found too easy to understand? If so, can you elaborate on this?
          </label>
          <textarea
            className="form-control"
            rows="4"
            value={userResponses.easyUnderstandingParts}
            onChange={(e) => handleInputChange("easyUnderstandingParts", e.target.value)}
            readOnly={isFeedbackSubmitted}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            What parts of the course did you find most useful?
          </label>
          <textarea
            className="form-control"
            rows="4"
            value={userResponses.usefulParts}
            onChange={(e) => handleInputChange("usefulParts", e.target.value)}
            readOnly={isFeedbackSubmitted}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Do you have any additional feedback or suggestions?</label>
          <textarea
            className="form-control"
            rows="4"
            value={userResponses.additionalFeedback}
            onChange={(e) => handleInputChange("additionalFeedback", e.target.value)}
            readOnly={isFeedbackSubmitted}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Signature:</label>
          {savedSignature ? (
            <img
              src={savedSignature}
              alt="Signature"
              style={{
                border: "1px solid #000",
                backgroundColor: "#fff",
                width: 350,
                height: 170,
                padding: 25,
              }}
            />
          ) : (
            <SignatureCanvas
              ref={signaturePadRef}
              penColor="black"
              canvasProps={{
                width: 500,
                height: 200,
                className: "signature-canvas",
                style: { border: "1px solid #000", backgroundColor: "#fff" },
              }}
              clearOnResize={false}
            />
          )}
          {!isFeedbackSubmitted && (
            <button
              type="button"
              className="btn btn-secondary mt-2"
              onClick={() => signaturePadRef.current.clear()}
            >
              Clear Signature
            </button>
          )}
        </div>

        {submittedDate  && isFeedbackSubmitted && (
          <div className="mb-3">
            <strong>Feedback Submitted Date: </strong>{userResponses.submittedDate}
          </div>
        )}

        {!isFeedbackSubmitted && (
          <button
            type="button"
            className="btn btn-primary mt-4"
            onClick={handleSubmitFeedback}
          >
            Submit Feedback
          </button>
        )}
      </form>

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
    </div>
  );
};

export default Feedback;
